import { useEffect, useState } from 'react';
import { gql, useQuery } from '@apollo/client';
import { GetWsPayPaymentDetailsQuery, GetWsPayPaymentDetailsQueryVariables } from 'src/common/api/apollo/types/types';
import { useGeneralSoContext } from 'src/common/context';
import { useGetOfferById } from './useGetOffer.query';

/**
 * Get WSPay payment details for payment form submission
 *
 * @param id - Encrypted Offer ID
 * @param paymentType - Payment method: 'CreditCard', 'KeksPay', 'Aircash', etc.
 * @param installments - Optional; when provided, backend sets PaymentPlan (e.g. 1 → '0000')
 * @returns WSPay form data to submit for payment
 *
 * @example
 * const [getWSPayPaymentDetails, { loading, error, data }] = useGetWSPayPaymentDetails();
 * const details = await getWSPayPaymentDetails(offerId, 'CreditCard', 3);
 */
export const GET_WS_PAY_PAYMENT_DETAILS = gql`
  query GetWSPayPaymentDetails($id: String!, $paymentType: String!, $installments: Int) {
    getWSPayPaymentDetails(id: $id, paymentType: $paymentType, installments: $installments) {
      ShopID
      ShoppingCartID
      TotalAmount
      Signature
      PaymentPlan
      ReturnURL
      CancelURL
      ReturnErrorURL
      CustomerFirstName
      CustomerLastName
      CustomerAddress
      CustomerCity
      CustomerZIP
      CustomerCountry
      CustomerPhone
      CustomerEmail
      PaymentMethod
      Iframe
      IframeResponseTarget
      Version
    }
  }
`;

export const useGetWSPayPaymentDetails = (paymentType = 'CreditCard') => {
  const { offerId } = useGeneralSoContext();
  const { offer } = useGetOfferById();
  const [error, setError] = useState<Error | null>(null);

  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    setIsInitialized(true);
  }, []);
  
  const { data: result, loading, refetch } = useQuery<GetWsPayPaymentDetailsQuery, GetWsPayPaymentDetailsQueryVariables>(
    GET_WS_PAY_PAYMENT_DETAILS,
    {
      variables: {
        id: offerId!,
        paymentType: paymentType,
        installments: offer?.productTypeSpecific.payment.paymentMethod === 'cardInstallments' ? 12 : 1,
      },
      skip: !offerId || !offer,
      onError: (err) => setError(err),
      onCompleted: () => setError(null),
    }
  );

  const isLoading = !isInitialized || loading;

  return {
    data: offerId ? result?.getWSPayPaymentDetails : undefined,
    loading: isLoading,
    error,
    refetch
  };
};
